import useSWR from 'swr'

import { FetchError } from '../../../data/fetchError'
import { FetchResponse } from '../../../data/fetchResponse'
import { FetchResult } from '../../../data/fetchResult'
import { fetchJson } from '../../../data/fetchers'
import {
  DeliveryForecastDespatchAdvice,
  DeliveryForecastDespatchAdviceItem,
  DeliveryForecastDespatchAdviceItemBatch,
  DeliveryForecastDespatchAdvicePackaging,
  DeliveryForecastDespatchAdvicePackagingItem,
  WebEdiDeliveryForecastDto,
} from '../../../data/models'
import { useConfigContext } from '../../ui-config'
import { PackagingNode } from './model'

// Warning pulling (refreshInterval) causes high costs in Azure!
// This was activated to show the user current data to test it
// but this needs to be replaced with optimistic UI with SignalR in the future,
// see ticket AN05873-230.

export function useAllDeliveryForecastDespatchAdvices(): FetchResult<
  WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>[]
> {
  const { pollingConfig } = useConfigContext()
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>[]>,
    FetchError
  >(
    `/DeliveryForecastDespatchAdvices?includeItems=true`,
    fetchJson<WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>[]>,
    {
      refreshInterval: pollingConfig.intervalInSeconds * 1000,
    },
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastDespatchAdviceById(
  despatchAdviceId: string,
): FetchResult<WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>>,
    FetchError
  >(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}`,
    fetchJson<WebEdiDeliveryForecastDto<DeliveryForecastDespatchAdvice>>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

/**
 * Items
 */
export function useAllDeliveryForecastDespatchAdviceItems(
  despatchAdviceId: string,
): FetchResult<DeliveryForecastDespatchAdviceItem[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdviceItem[]>,
    FetchError
  >(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items`,
    fetchJson<DeliveryForecastDespatchAdviceItem[]>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastDespatchAdviceItemById(
  despatchAdviceId: string,
  itemId: string,
): FetchResult<DeliveryForecastDespatchAdviceItem> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdviceItem>,
    FetchError
  >(
    `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}`,
    fetchJson<DeliveryForecastDespatchAdviceItem>,
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

/**
 * Batches
 */
export function useAllDeliveryForecastDespatchAdviceItemBatches(
  despatchAdviceId: string,
  itemId: string,
  calculateBatchesOpenQuantity: boolean = false,
): FetchResult<DeliveryForecastDespatchAdviceItemBatch[]> {
  let url = `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}/batches`

  if (calculateBatchesOpenQuantity)
    url = `${url}/?calculateBatchesOpenQuantity=true`

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdviceItemBatch[]>,
    FetchError
  >(url, fetchJson<DeliveryForecastDespatchAdviceItemBatch[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastDespatchAdviceItemBatchById(
  despatchAdviceId: string,
  itemId: string,
  batchId: string,
  calculateBatchesOpenQuantity: boolean = false,
): FetchResult<DeliveryForecastDespatchAdviceItemBatch> {
  let url = `/DeliveryForecastDespatchAdvices/${despatchAdviceId}/Items/${itemId}/batches/${batchId}`

  if (calculateBatchesOpenQuantity)
    url = `${url}/?calculateBatchesOpenQuantity=true`

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdviceItemBatch>,
    FetchError
  >(url, fetchJson<DeliveryForecastDespatchAdviceItemBatch>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

/**
 * Packaging
 */
export function useAllDeliveryForecastDespatchAdviceItemPackagings(
  despatchAdviceId: string,
  itemId: string,
): FetchResult<DeliveryForecastDespatchAdvicePackaging[]> {
  const url =
    despatchAdviceId && itemId
      ? `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings`
      : null
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdvicePackaging[]>,
    FetchError
  >(url, fetchJson<DeliveryForecastDespatchAdvicePackaging[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastDespatchAdviceItemPackagingById(
  despatchAdviceId?: string,
  itemId?: string,
  packagingId?: string,
): FetchResult<DeliveryForecastDespatchAdvicePackaging> {
  const url =
    despatchAdviceId && itemId && packagingId
      ? `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${packagingId}`
      : null

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdvicePackaging>,
    FetchError
  >(url, fetchJson<DeliveryForecastDespatchAdvicePackaging>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useAllDeliveryForecastDespatchAdviceItemPackagingItems(
  despatchAdviceId: string,
  itemId?: string,
  packagingId?: string,
): FetchResult<PackagingNode[]> {
  const url =
    despatchAdviceId && itemId && packagingId
      ? `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${packagingId}/items?includeChildren=true`
      : null

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<PackagingNode[]>,
    FetchError
  >(url, fetchJson<PackagingNode[]>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useDeliveryForecastDespatchAdviceItemPackagingItemById(
  despatchAdviceId: string,
  itemId: string,
  packagingId: string,
  id?: string | null,
): FetchResult<DeliveryForecastDespatchAdvicePackagingItem> {
  const url =
    despatchAdviceId && itemId && packagingId && id
      ? `/deliveryforecastdespatchadvices/${despatchAdviceId}/items/${itemId}/packagings/${packagingId}/items/${id}`
      : null

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<DeliveryForecastDespatchAdvicePackagingItem>,
    FetchError
  >(url, fetchJson<DeliveryForecastDespatchAdvicePackagingItem>)

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}
